import { FunctionComponent, ReactNode, useEffect, useState } from 'react'
import { sortBySuggestPriority } from '../suggestTableUtils'
import styles from './suggestParentTable.module.scss'
import Indicator from '@/components/_custom/Indicator/indicator'
import Desktop from '@/modules/desktopMobile/desktop'
import Title from '@/components/_custom/Title/title'
import { QuickSearchToolbar, StyledDataGrid } from '@/components/_custom/DataGrid'
import { frFR } from '@mui/x-data-grid/locales'
import Mobile from '@/modules/desktopMobile/mobile'
import { useAuth } from '@/modules/context/AuthContext'
import { useTranslation } from 'react-i18next'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { GridColDef } from '@mui/x-data-grid'
import { ChildCare, Close, Favorite, FavoriteBorder, LinkSharp } from '@mui/icons-material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt'
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import Loading from '@/components/Loading/Loading'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import {
    GET_DISLIKED_SUGGEST_PARENTS,
    GET_SUGGEST_PARENTS,
    SET_DISLIKE_SUGGEST,
    SET_LIKE_SUGGEST
} from '@/modules/API/suggest/suggestHandler'
import CALDParentSettings from './CALDParentSettings'
import { Role } from '@/types/Role'
import ChildListWithoutDaycareDialog from './Dialog/childListWithoutDaycareDialog'
import { Child } from '@/types/Child'
import Comment from '@mui/icons-material/Comment'
import ConfirmAssociateParentDialog from './Dialog/confirmAssociateParentDialog'
import EditInternalNoteDialog from '../Dialog/editInternalNoteDialog'
import { SuggestParentProfile, SuggestSettingsResponse } from '@/types/Suggest'
import { GET_SUGGEST_SETTINGS } from '@/modules/API/daycare/daycareSuggestHandler'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import moment from 'moment'
import UserInput from '@/components/_custom/UserInput/userInput'
import { hasChildInAgeRange } from './childAgeFilter'

interface SuggestParentTableProps {
    switchTab: number
    showAssMat: boolean
}

type DataTable = {
    id: number
    userId?: number
    childCount: number
    childsName: string
    childsList: Child[]
    name: string
    email: string
    phone: string
    city: string
    zip: string
    internalNote?: string
    liked: boolean
    disliked: boolean
}

const SuggestParentTable: FunctionComponent<SuggestParentTableProps> = ({
    switchTab,
    showAssMat
}: SuggestParentTableProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [allSuggest, setAllSuggest] = useState<SuggestParentProfile[]>([])
    const [countLikedSuggest, setCountLikedSuggest] = useState<number>(0)
    const [countCommentedSuggest, setCountCommentedSuggest] = useState<number>(0)

    const [tableData, setTableData] = useState<DataTable[]>([])
    const [ageMin, setAgeMin] = useState<string>('0')
    const [ageMax, setAgeMax] = useState<string>('3')

    const { data: dataSuggestSettings } = useQuery<{ getSuggestSettings: SuggestSettingsResponse }>(
        GET_SUGGEST_SETTINGS,
        {
            variables: { daycareId: user?.currentDaycare?.id },
            skip: !user?.currentDaycare?.id,
            fetchPolicy: 'network-only'
        }
    )

    const defaultAgeMin: string = ((dataSuggestSettings?.getSuggestSettings.parentMinMonth ?? 0) / 12).toString()
    const defaultAgeMax: string = ((dataSuggestSettings?.getSuggestSettings.parentMaxMonth ?? 36) / 12).toString()

    useEffect(() => {
        setAgeMin(defaultAgeMin)
        setAgeMax(defaultAgeMax)
    }, [user?.currentDaycare?.id, defaultAgeMin, defaultAgeMax])

    const [isChildDialogOpen, setChildDialogOpen] = useState(false)
    const [childs, setChilds] = useState<Child[]>([])

    const [isInternalNoteDialogOpen, setInternalNoteDialogOpen] = useState(false)
    const [userIdAndInternalNote, setUserIdAndInternalNote] = useState<
        { userId: number; internalNote: string } | undefined
    >(undefined)

    const [isConfirmAssociateDialogOpen, setConfirmAssociateDialogOpen] = useState(false)
    const [emailToAssociate, setEmailToAssociate] = useState<string>('')
    const [nameToAssociate, setNameToAssociate] = useState<string>('')

    const [CALDRequest, setCALDRequest] = useState<boolean>(false)

    const [showMasked, setShowMasked] = useState<boolean>(false)
    const [maskedData, setMaskedData] = useState<SuggestParentProfile[]>([])

    const canEditSuggest: boolean = user?.currentDaycareRights.canWriteSuggestions || false

    const headerClassName: string = user?.role === Role.parent ? styles.HeaderParent : styles.HeaderDaycare

    const {
        loading: loadingGetSuggestParent,
        data: dataGetSuggestParent,
        error: errorGetSuggestParent,
        refetch: refetchGetSuggestParent
    } = useQuery(GET_SUGGEST_PARENTS, {
        variables: {
            daycareId: user?.currentDaycare?.id,
            isNanny: showAssMat && switchTab === 1
        }
    })

    useEffect(() => {
        if (errorGetSuggestParent) {
            setAllSuggest([])
        }
    }, [errorGetSuggestParent])

    useEffect(() => {
        if (dataGetSuggestParent) {
            setCALDRequest(false)
            setAllSuggest([...dataGetSuggestParent.getSuggestParents].sort(sortBySuggestPriority))
        }
    }, [dataGetSuggestParent])

    const [loadingRefetch, setLoadingRefetch] = useState(false)

    async function handleRefetch(): Promise<void> {
        setLoadingRefetch(true)
        const { data } = await refetchGetSuggestParent()
        if (data) {
            setAllSuggest([...data.getSuggestParents].sort(sortBySuggestPriority))
        }
        setLoadingRefetch(false)
    }

    const [getDislikedParents, { loading: loadingDisliked }] = useLazyQuery(GET_DISLIKED_SUGGEST_PARENTS, {
        fetchPolicy: 'network-only',
        variables: { daycareId: user?.currentDaycare?.id, isNanny: showAssMat && switchTab === 1 },
        onCompleted: (data: { getDislikedSuggestParents: SuggestParentProfile[] }) =>
            setMaskedData(data.getDislikedSuggestParents)
    })

    const [setDislikeSuggestMutation] = useMutation(SET_DISLIKE_SUGGEST, {
        onError: () => showNotificationError(t('suggestLikedError'))
    })

    const [likeSuggest] = useMutation(SET_LIKE_SUGGEST, {
        onCompleted: () => {
            handleRefetch()
            showNotificationSuccess(t('suggestLiked'))
        },
        onError: () => {
            showNotificationError(t('suggestLikedError'))
        }
    })

    useEffect(() => {
        if (allSuggest.length > 0) {
            setCountLikedSuggest(getCountLikedSuggest(allSuggest))
            setCountCommentedSuggest(getCountCommentedSuggest(allSuggest))
            setTableData(getTableData(allSuggest))
        } else {
            setCountLikedSuggest(0)
            setCountCommentedSuggest(0)
            setTableData([])
        }
    }, [allSuggest])

    async function handleDislike(userId: number, currentDisliked: boolean): Promise<void> {
        if (!canEditSuggest) return
        await setDislikeSuggestMutation({
            variables: {
                userId,
                daycareId: user?.currentDaycare?.id,
                disliked: !currentDisliked
            }
        })
        if (!currentDisliked) {
            const profile: SuggestParentProfile | undefined = allSuggest.find((s) => s.parent.userId === userId)
            setTableData((prev) => prev.filter((row) => row.userId !== userId))
            setAllSuggest((prev: SuggestParentProfile[]) => prev.filter((s) => s.parent.userId !== userId))
            if (profile) setMaskedData((prev) => [...prev, { ...profile, disliked: true }])
        } else {
            const profile: SuggestParentProfile | undefined = maskedData.find((s) => s.parent.userId === userId)
            setMaskedData((prev) => prev.filter((s) => s.parent.userId !== userId))
            if (profile) setAllSuggest((prev) => [...prev, { ...profile, disliked: false }])
        }
    }

    async function handleLike(userId: number, liked: boolean): Promise<void> {
        if (canEditSuggest) {
            await likeSuggest({
                variables: {
                    userId: userId,
                    daycareId: user?.currentDaycare?.id,
                    liked: !liked
                }
            })
        }
    }

    const columnsWeb: GridColDef[] = [
        {
            field: 'childCount',
            headerName: t('childCount'),
            headerClassName: headerClassName,
            width: 80,
            maxWidth: 200,
            minWidth: 40,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer} onClick={() => openChildList(params.row.childsList)}>
                    {params?.row.childCount ? params.row.childCount : 0}
                    <ChildCare className={styles.Icon} />
                </div>
            )
        },
        {
            field: 'childsName',
            headerName: t('childs'),
            headerClassName: headerClassName,
            width: 1,
            minWidth: 1,
            maxWidth: 1,
            renderCell: (params) => (
                <div className={styles.Hide}>{params.row.childsName ? params.row.childsName : ''}</div>
            )
        },
        {
            field: 'name',
            headerName: t('name'),
            headerClassName: headerClassName,
            width: 200,
            minWidth: 200,
            maxWidth: 400,
            renderCell: (params) => (
                <a
                    href={`/adults?parent=${params.row.id}`}
                    onClick={(e) => {
                        if (!(e.ctrlKey || e.metaKey)) {
                            e.preventDefault()
                            navigate(`/adults?parent=${params.row.id}`)
                        }
                    }}
                    className={`${styles.InfoContainer} ${styles.Clickable}`}
                >
                    {params.row.name}
                </a>
            )
        },
        {
            field: 'email',
            headerName: t('email'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 400,
            minWidth: 200,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.email ? (
                        <a href={`mailto:${params.row.email}`}>{params.row.email}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'phone',
            headerName: t('phone'),
            headerClassName: headerClassName,
            width: 120,
            maxWidth: 400,
            minWidth: 100,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.phone ? (
                        <a href={`tel:${params.row.phone}`}>{params.row.phone.match(/.{1,2}/g).join(' ')}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'city',
            headerName: t('city'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 400,
            minWidth: 100,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.city ? params.row.city : <Close className={styles.RedIcon} />}
                </div>
            )
        },
        {
            field: 'zip',
            headerName: t('zipCode'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 400,
            minWidth: 100,
            flex: 1
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 170,
            maxWidth: 200,
            minWidth: 100,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer}>

                    <a
                        onClick={() => openInternalNoteDialog(params.row.userId, params.row.internalNote)}
                        className={styles.Link}
                        >
                        <Comment 
                            className={canEditSuggest ? styles.Icon : styles.IconWithoutHover}
                            />
                    </a>

                    <a onClick={() => handleLike(params.row.userId, params.row.liked)} className={styles.Link}>
                        {params.row.liked ? (
                            <Favorite className={canEditSuggest ? styles.HeartIcon : styles.HeartIconWithoutHover} />
                        ) : (
                            <FavoriteBorder className={canEditSuggest ? styles.Icon : styles.IconWithoutHover} />
                        )}
                    </a>
                    {canEditSuggest && !showMasked && (
                        <a
                            onClick={() => handleDislike(params.row.userId, params.row.disliked)}
                            className={styles.Link}
                        >
                            <ThumbDownAltIcon className={params.row.disliked ? styles.ActiveIcon : styles.Icon} />
                        </a>
                    )}
                    {canEditSuggest && showMasked && (
                        <a onClick={() => handleDislike(params.row.userId, true)} className={styles.Link}>
                            <ThumbUpAltIcon className={styles.Icon} />
                        </a>
                    )}
                    <a
                        href={`/adults?parent=${params.row.id}`}
                        onClick={(e) => {
                            if (!(e.ctrlKey || e.metaKey)) {
                                e.preventDefault()
                                navigate(`/adults?parent=${params.row.id}`)
                            }
                        }}
                        className={styles.Link}
                    >
                        <VisibilityIcon className={styles.Icon} />
                    </a>
                    {canEditSuggest && user?.currentDaycareRights.canWriteFamilies && (
                        <a
                            onClick={() => {
                                setEmailToAssociate(params.row.email)
                                setNameToAssociate(params.row.name)
                                setConfirmAssociateDialogOpen(true)
                            }}
                            className={styles.Link}
                        >
                            <LinkSharp className={styles.Icon} />
                        </a>
                    )}
                </div>
            )
        }
    ]

    const columnsWebCALD: GridColDef[] = [
        {
            field: 'childCount',
            headerName: t('childCount'),
            headerClassName: headerClassName,
            width: 100,
            maxWidth: 200,
            minWidth: 100,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer} onClick={() => openChildList(params.row.childsList)}>
                    {params?.row.childCount ? params.row.childCount : 0}
                    <ChildCare className={styles.Icon} />
                </div>
            )
        },
        {
            field: 'childsName',
            headerName: t('childs'),
            headerClassName: headerClassName,
            width: 1,
            minWidth: 1,
            maxWidth: 1,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.Hide}>{params.row.childsName ? params.row.childsName : ''}</div>
            )
        },
        {
            field: 'name',
            headerName: t('name'),
            headerClassName: headerClassName,
            width: 200,
            minWidth: 200,
            maxWidth: 400,
            flex: 1,
            renderCell: (params) => (
                <a
                    href={`/adults?parent=${params.row.id}`}
                    onClick={(e) => {
                        if (!(e.ctrlKey || e.metaKey)) {
                            e.preventDefault()
                            navigate(`/adults?parent=${params.row.id}`)
                        }
                    }}
                    className={`${styles.InfoContainer} ${styles.Clickable}`}
                >
                    {params.row.name}
                </a>
            )
        },
        {
            field: 'email',
            headerName: t('email'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 400,
            minWidth: 200,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.email ? (
                        <a href={`mailto:${params.row.email}`}>{params.row.email}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'phone',
            headerName: t('phone'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 400,
            minWidth: 100,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.phone ? (
                        <a href={`tel:${params.row.phone}`}>{params.row.phone.match(/.{1,2}/g).join(' ')}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'city',
            headerName: t('city'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 400,
            minWidth: 100,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.city ? params.row.city : <Close className={styles.RedIcon} />}
                </div>
            )
        },
        {
            field: 'zip',
            headerName: t('zipCode'),
            headerClassName: headerClassName,
            width: 70,
            maxWidth: 100,
            minWidth: 50,
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 250,
            maxWidth: 300,
            minWidth: 100,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <a
                        href={`/adults?parent=${params.row.id}`}
                        onClick={(e) => {
                            if (!(e.ctrlKey || e.metaKey)) {
                                e.preventDefault()
                                navigate(`/adults?parent=${params.row.id}`)
                            }
                        }}
                        className={styles.Link}
                    >
                        <VisibilityIcon className={styles.Icon} />
                    </a>
                </div>
            )
        }
    ]

    const columnsMobile: GridColDef[] = [
        {
            field: 'childCount',
            headerName: t('childs'),
            headerClassName: headerClassName,
            width: 120,
            maxWidth: 200,
            minWidth: 50,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer} onClick={() => openChildList(params.row.childsList)}>
                    {params?.row.childCount ? params.row.childCount : 0}
                    <ChildCare className={styles.Icon} />
                </div>
            )
        },
        {
            field: 'childsName',
            headerName: t('childs'),
            headerClassName: headerClassName,
            width: 1,
            minWidth: 1,
            maxWidth: 1,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.Hide}>{params.row.childsName ? params.row.childsName : ''}</div>
            )
        },
        {
            field: 'name',
            headerName: t('name'),
            headerClassName: headerClassName,
            width: 100,
            minWidth: 100,
            maxWidth: 250,
            flex: 1,
            renderCell: (params) => (
                <div
                    className={`${styles.InfoContainer} ${styles.Clickable}`}
                    onClick={() => navigate(`/adults?parent=${params.row.id}`)}
                >
                    {params.row.name}
                </div>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 100,
            maxWidth: 250,
            minWidth: 120,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <a onClick={() => handleLike(params.row.userId, params.row.liked)} className={styles.Link}>
                        {params.row.liked ? (
                            <Favorite className={styles.HeartIcon} />
                        ) : (
                            <FavoriteBorder className={styles.Icon} />
                        )}
                    </a>
                    {canEditSuggest && !showMasked && (
                        <a
                            onClick={() => handleDislike(params.row.userId, params.row.disliked)}
                            className={styles.Link}
                        >
                            <ThumbDownAltIcon className={params.row.disliked ? styles.ActiveIcon : styles.Icon} />
                        </a>
                    )}
                    {showMasked && (
                        <a onClick={() => handleDislike(params.row.userId, true)} className={styles.Link}>
                            <ThumbUpAltIcon className={styles.Icon} />
                        </a>
                    )}
                    <a
                        href={`/adults?parent=${params.row.id}`}
                        onClick={(e) => {
                            if (!(e.ctrlKey || e.metaKey)) {
                                e.preventDefault()
                                navigate(`/adults?parent=${params.row.id}`)
                            }
                        }}
                        className={styles.Link}
                    >
                        <VisibilityIcon className={styles.Icon} />
                    </a>
                    {canEditSuggest && user?.currentDaycareRights.canWriteFamilies && (
                        <a
                            onClick={() => {
                                setEmailToAssociate(params.row.email)
                                setNameToAssociate(params.row.name)
                                setConfirmAssociateDialogOpen(true)
                            }}
                            className={styles.Link}
                        >
                            <LinkSharp className={styles.Icon} />
                        </a>
                    )}
                </div>
            )
        }
    ]

    function openChildList(childs: Child[]): void {
        if (childs && childs.length > 0) {
            setChilds(childs)
            setChildDialogOpen(true)
        } else {
            showNotificationSuccess(t('noChilds'))
        }
    }

    function openInternalNoteDialog(userId: number, internalNote: string): void {
        if (canEditSuggest) {
            setUserIdAndInternalNote({
                userId: userId,
                internalNote: internalNote
            })
            setInternalNoteDialogOpen(true)
        }
    }

    function getTableData(suggests: SuggestParentProfile[]): DataTable[] {
        return suggests.map((suggest: SuggestParentProfile): DataTable => {
            return {
                id: suggest.parent.id,
                userId: suggest.parent.userId,
                childCount: suggest.parent.childs.length,
                childsName: suggest.parent.childs
                    .map(
                        (child: Child): string =>
                            `${child.firstname} - ${moment(child.birthdate, 'DD-MM-YYYY').format('YYYY')}`
                    )
                    .join(', '),
                childsList: suggest.parent.childs,
                name: suggest.parent.lastname + ' ' + suggest.parent.firstname,
                city: suggest.parent.city,
                zip: suggest.parent.zip,
                email: suggest.parent.email,
                phone: suggest.parent.phone,
                internalNote: suggest.internalNote,
                liked: suggest.liked || false,
                disliked: suggest.disliked || false
            }
        })
    }

    function resetFilter(): void {
        setAgeMin(defaultAgeMin)
        setAgeMax(defaultAgeMax)
        setShowMasked(false)
        setTableData(getTableData(allSuggest))
    }

    function filterMasked(): void {
        setShowMasked(true)
        getDislikedParents()
    }

    function filterLiked(): void {
        setTableData(getTableData(allSuggest).filter((row) => row.liked))
    }

    function getCountLikedSuggest(suggests: SuggestParentProfile[]): number {
        return suggests.filter((suggest) => suggest.liked).length
    }

    function filterCommented(): void {
        setTableData(getTableData(allSuggest).filter((row) => row.internalNote !== ''))
    }

    function getCountCommentedSuggest(suggests: SuggestParentProfile[]): number {
        return suggests.filter((suggest) => suggest.internalNote !== '').length
    }

    function getTitle(): string {
        if (switchTab === 0) {
            return t('family')
        } else {
            return t('assMatShort')
        }
    }

    const displayData: DataTable[] = (showMasked ? getTableData(maskedData) : tableData).filter((row) =>
        hasChildInAgeRange(row.childsList, ageMin, ageMax)
    )

    const ageFilters: ReactNode = (
        <div className={styles.AgeFilters}>
            <UserInput
                type={'number'}
                value={ageMin}
                setValue={setAgeMin}
                format={'num'}
                minNumber={0}
                maxNumber={ageMax === '' ? 100 : Number(ageMax)}
                label={t('ageMinimum')}
                verify={'number'}
                className={styles.UserInput}
                endAdornment={t('yearsOld')}
            />
            <UserInput
                type={'number'}
                value={ageMax}
                setValue={setAgeMax}
                format={'num'}
                minNumber={Number(ageMin)}
                maxNumber={100}
                verify={'number'}
                className={styles.UserInput}
                label={t('ageMaximum')}
                endAdornment={t('yearsOld')}
            />
        </div>
    )

    return (
        <>
            {user?.role === Role.cald && (
                <CALDParentSettings
                    setAllSuggest={setAllSuggest}
                    isNanny={showAssMat && switchTab === 1}
                    setCALDRequest={setCALDRequest}
                    ageMin={ageMin}
                    setAgeMin={setAgeMin}
                    ageMax={ageMax}
                    setAgeMax={setAgeMax}
                />
            )}
            <div className={styles.IndicatorsContainer}>
                <a onClick={resetFilter} className={styles.Clickable}>
                    <Indicator count={allSuggest.length} backgroundWhite text={t('all')} />
                </a>
                <a onClick={filterLiked} className={styles.Clickable}>
                    <Indicator count={countLikedSuggest} backgroundWhite text={t('liked')} />
                </a>
                {countCommentedSuggest > 0 && (
                    <a onClick={filterCommented} className={styles.Clickable}>
                        <Indicator count={countCommentedSuggest} backgroundWhite text={t('comment')} />
                    </a>
                )}
                {canEditSuggest && (
                    <a onClick={showMasked ? resetFilter : filterMasked} className={styles.Clickable}>
                        <Indicator count={maskedData.length} backgroundWhite text={t('masked')} />
                    </a>
                )}
            </div>
            {(loadingGetSuggestParent || loadingRefetch || loadingDisliked) && <Loading overall />}
            {(dataGetSuggestParent || user?.role === Role.cald) && (
                <div className={styles.DataGridContainer}>
                    <div className={styles.Title}>
                        <Title text={t('mySuggests') + ' - ' + getTitle()} />
                    </div>
                    <Desktop>
                        <StyledDataGrid
                            autoHeight
                            columns={CALDRequest ? columnsWebCALD : columnsWeb}
                            rows={displayData}
                            disableDensitySelector
                            disableColumnMenu
                            slots={{ toolbar: QuickSearchToolbar }}
                            slotProps={{ toolbar: { actions: ageFilters } }}
                            showToolbar
                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                            rowHeight={80}
                            className={styles.DataGrid}
                            initialState={{
                                filter: {
                                    filterModel: {
                                        items: [],
                                        quickFilterExcludeHiddenColumns: false
                                    }
                                }
                            }}
                            columnVisibilityModel={{
                                childsName: false
                            }}
                            rowCount={displayData.length}
                            paginationMode={'server'}
                        />
                    </Desktop>
                    <Mobile>
                        <StyledDataGrid
                            autoHeight
                            columns={columnsMobile}
                            rows={displayData}
                            disableDensitySelector
                            disableColumnMenu
                            slots={{ toolbar: QuickSearchToolbar }}
                            slotProps={{ toolbar: { actions: ageFilters } }}
                            showToolbar
                            sx={{ '--DataGrid-overlayHeight': '300px' }}
                            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                            rowHeight={80}
                            className={styles.DataGrid}
                            initialState={{
                                filter: {
                                    filterModel: {
                                        items: [],
                                        quickFilterExcludeHiddenColumns: false
                                    }
                                }
                            }}
                            columnVisibilityModel={{
                                childsName: false
                            }}
                        />
                    </Mobile>
                </div>
            )}
            {errorGetSuggestParent && user?.role !== Role.cald && <ErrorMessage error={t('generalError')} />}
            {isChildDialogOpen && (
                <ChildListWithoutDaycareDialog
                    isDialogOpen={isChildDialogOpen}
                    handleCloseDialog={() => setChildDialogOpen(false)}
                    childs={childs}
                />
            )}
            {isConfirmAssociateDialogOpen && emailToAssociate !== '' && (
                <ConfirmAssociateParentDialog
                    open={isConfirmAssociateDialogOpen}
                    closeDialog={() => setConfirmAssociateDialogOpen(false)}
                    name={nameToAssociate}
                    email={emailToAssociate}
                    isNanny={showAssMat && switchTab === 1}
                    refetchAllSuggest={handleRefetch}
                />
            )}
            {isInternalNoteDialogOpen && userIdAndInternalNote && (
                <EditInternalNoteDialog
                    open={isInternalNoteDialogOpen}
                    closeDialog={() => setInternalNoteDialogOpen(false)}
                    internalNote={userIdAndInternalNote.internalNote}
                    userId={userIdAndInternalNote.userId}
                    refetchAllSuggest={handleRefetch}
                />
            )}
        </>
    )
}

export default SuggestParentTable
